import * as R from 'ramda';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// mui
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
// constants
import {
  ENUM_OK,
  ENUM_FAIL,
  ENUM_NONE,
  ENUM_IMPORTING,
  DEFAULT_DATE_TIME_FORMAT,
} from '../../../../constants';
// utilities
import { getDateWithFormat, calculateElapsedTime } from '../../../../utilities';
//////////////////////////////////////////////////

const ProcessDateText = ({ t, label, text }) => (
  <Typography
    ml='-20px'
    fontSize={12}
    component='div'
    variant='subtitle2'
    color='text.secondary'
    title={t(`labels.${label}`)}
  >
    {text}
  </Typography>
);

const ProgressLabelText = ({ t, label, error = false }) => (
  <Box display='flex' alignItems='center' title={t('labels.importedFileStatus')}>
    <Typography
      fontSize={20}
      component='div'
      variant='subtitle1'
      sx={{ color: t => (error ? t.colors.red : t.text.secondary) }}
    >
      {t(label)}
    </Typography>
  </Box>
);

const ProgressWithLabel = ({ t, fileData}) => {
  const {
    fileName,
    processedOn,
    processedStatus,
    processedEndDate,
    processedStartDate,
  } = fileData;

  const { days, hours, minutes } = calculateElapsedTime(processedStartDate);

  const getProcessStartedText = () => {
    const getDaysLabel = () => {
      if (R.gte(days, 2)) return `${days}${t('label.days')}, `;

      if (R.equals(days, 1)) return `${days}${t('label.day')}, `;

      return '';
    };

    const daysLabel = getDaysLabel();
    const hoursLabel = `${hours}${t('labels.hours')}, `;
    const minutesLabel = `${minutes}${t('labels.minutes')} `;

    return `${t('labels.started')} ${daysLabel}${hoursLabel}${minutesLabel}${t('labels.ago')}`;
  };

  if (R.equals(processedStatus, ENUM_NONE)) return <ProgressLabelText t={t} label='labels.fileNotImported' />;

  if (R.equals(processedStatus, ENUM_FAIL)) return <ProgressLabelText t={t} error={true} label='errors.fileImportFailed' />;

  return (
    <Box display='flex' alignItems='center' flexDirection='column' title={t('labels.importedFileStatus')}>
      <Typography
        ml='-20px'
        fontSize={12}
        maxWidth={220}
        component='div'
        title={fileName}
        overflow='hidden'
        textAlign='start'
        variant='subtitle1'
        whiteSpace='nowrap'
        color='text.secondary'
        textOverflow='ellipsis'
      >
        {fileName}
      </Typography>
      {
        R.equals(processedStatus, ENUM_IMPORTING) && (
          <Box display='flex' alignItems='center'>
            <Box mr={1} width={220}>
              <LinearProgress
                value={processedOn}
                variant='determinate'
                sx={{ height: 6, borderRadius: 4 }}
              />
            </Box>
            <Box minWidth={35}>
              <Typography variant='caption' component='div' color='text.secondary'>
                {`${Math.round(processedOn)}%`}
              </Typography>
            </Box>
          </Box>
        )
      }
      {
        R.equals(processedStatus, ENUM_OK) && (
          <ProcessDateText
            t={t}
            label='importedFileDate'
            text={getDateWithFormat(processedEndDate, DEFAULT_DATE_TIME_FORMAT)}
          />
        )
      }
      {
        R.equals(processedStatus, ENUM_IMPORTING) && (
          <ProcessDateText t={t} label='importFileStarted' text={getProcessStartedText()} />
        )
      }
    </Box>
  );
};

const FileUploadProgress = ({ fileData, checkFileData }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setInterval(() => {
      checkFileData();
    }, 150000);

    return () => {
      clearInterval(timer);
    };
  }, [fileData, checkFileData]);

  return <ProgressWithLabel t={t} fileData={fileData} />;
};

export default FileUploadProgress;
